import type { LensCard, LensCardData, LensCardLayout } from '~/types/leLens';

import { smartCardFrame } from './cardArrangement';
import { cardHeight } from './cardLayout';
import { frameRowSpan } from './cards/cardGrid';

/** The saved canvas uses a stable 12-column coordinate system, not pixels. */
export const CANVAS_COLUMNS = 12;

/** A phone reads one card at a time; every desktop keeps the authored 12-column board. */
export const CANVAS_STACK_MAX_WIDTH_PX = 560;

/** Individually placed metrics keep their established compact default. */
const DEFAULT_METRIC_CANVAS_HEIGHT = frameRowSpan(112);
const MINIMUM_METRIC_CANVAS_HEIGHT = frameRowSpan(88);
// An arranged KPI must fit its value, comparison and any visible contract
// footer. The smaller manual floor remains an editor's explicit choice.
const SMART_METRIC_CANVAS_HEIGHT = frameRowSpan(136);

/**
 * Never reinterpret a desktop Lens while its editor lane opens or closes.
 *
 * The old 4/8/12 breakpoints projected and compacted a saved composition on
 * every ResizeObserver update. That made a deliberate card position look as
 * though it reverted, then settled again. Desktop cards now retain their
 * authored geometry; a true phone gets a read-only single-column view.
 */
export function canvasColumnsForWidth(width: number): number {
  return width > 0 && width <= CANVAS_STACK_MAX_WIDTH_PX ? 1 : CANVAS_COLUMNS;
}

/** Keep chart labels, actions and content usable when a card is resized. */
export function minimumCanvasWidth(
  card: Pick<LensCard, 'type'> & Partial<Pick<LensCard, 'title'>>,
  _data?: LensCardData,
): number {
  return card.type === 'metric' ? 2 : 4;
}

export function frameForCard(card: LensCard, data?: LensCardData): LensCardLayout {
  const smart = smartCardFrame(card, data);
  // A partial answer is a safety state, not a preference. It must reserve the
  // frame required by its warning even when an old Lens saved a compact size.
  const safetyFrame = Boolean(data?.truncated);
  const size = safetyFrame ? smart.size : (card.size ?? smart.size);
  const height = safetyFrame ? smart.height : (card.height ?? smart.height);
  const width = Math.max(minimumCanvasWidth(card, data), { small: 2, medium: 4, large: 6, full: CANVAS_COLUMNS }[size]);
  return {
    x: 0,
    y: 0,
    w: width,
    h: Math.max(
      minimumCanvasHeight(card),
      card.type === 'metric' && height === 'short'
        ? DEFAULT_METRIC_CANVAS_HEIGHT
        : frameRowSpan(cardHeight(size, height).card),
    ),
  };
}

/**
 * An editor can deliberately shrink a KPI below its arranged height. Text
 * cards also need a smaller floor than charts, with longer content scrolling.
 */
export function minimumCanvasHeight(card?: Pick<LensCard, 'type' | 'size' | 'height'>): number {
  if (card?.type === 'metric') return MINIMUM_METRIC_CANVAS_HEIGHT;
  // Text needs a header and a few lines, rather than a chart's plot area.
  return card?.type === 'insight' ? frameRowSpan(136) : frameRowSpan(280);
}

export function framesOverlap(left: LensCardLayout, right: LensCardLayout): boolean {
  return (
    left.x < right.x + right.w && left.x + left.w > right.x && left.y < right.y + right.h && left.y + left.h > right.y
  );
}

function clampFrame(frame: LensCardLayout): LensCardLayout {
  const w = Math.min(CANVAS_COLUMNS, Math.max(1, Math.round(frame.w)));
  return {
    x: Math.min(CANVAS_COLUMNS - w, Math.max(0, Math.round(frame.x))),
    y: Math.max(0, Math.round(frame.y)),
    w,
    h: Math.max(1, Math.round(frame.h)),
    ...(frame.by ? { by: frame.by } : {}),
  };
}

function firstVacantFrame(frame: LensCardLayout, occupied: Array<LensCardLayout>, startY = 0): LensCardLayout {
  const candidate = clampFrame({ ...frame, y: startY });
  // Stored frames are an editor's explicit decision. Preserve their exact
  // origin when it is available; only flow a card when it genuinely collides.
  if (!occupied.some((other) => framesOverlap(candidate, other))) {
    return candidate;
  }
  for (let y = candidate.y; y < 10000; y += 1) {
    for (let x = 0; x <= CANVAS_COLUMNS - candidate.w; x += 1) {
      const next = { ...candidate, x, y };
      if (!occupied.some((other) => framesOverlap(next, other))) {
        return next;
      }
    }
  }
  return candidate;
}

export function resolveCanvasLayouts(cards: Array<LensCard>, dataByCardId: Map<string, LensCardData>): Array<LensCard> {
  const bySection = new Map<string, Array<LensCard>>();
  cards.forEach((card) => {
    const section = card.section ?? '__ungrouped';
    bySection.set(section, [...(bySection.get(section) ?? []), card]);
  });
  const frames = new Map<string, LensCardLayout>();
  bySection.forEach((group) => {
    const authored = group.filter((card) => card.layout);
    const automatic = group.filter((card) => !card.layout);
    const arranged = smartArrangeCards(automatic, dataByCardId);
    const occupied = authored.map((card) => card.layout!);
    const requested = [...authored, ...arranged].map((card) => {
      const safe = frameForCard(card, dataByCardId.get(card.id));
      const frame = authored.includes(card) ? card.layout! : firstVacantFrame(card.layout ?? safe, occupied);
      occupied.push(frame);
      const partial = dataByCardId.get(card.id)?.truncated;
      return {
        i: card.id,
        ...clampFrame({
          ...frame,
          w: Math.max(frame.w, minimumCanvasWidth(card), partial ? safe.w : 0),
          h: Math.max(frame.h, minimumCanvasHeight(card), partial ? safe.h : 0),
        }),
      };
    });
    // `smartArrangeCards` has already packed automatic frames. Re-compacting
    // here would pull centred rows back to the left, undoing the intentional
    // alignment before the canvas renders.
    requested.forEach(({ i, ...frame }) => {
      frames.set(i, frame);
    });
  });
  return cards.map((card) => ({ ...card, layout: frames.get(card.id) ?? frameForCard(card) }));
}

function cardsPerBalancedRow(count: number): Array<number> {
  if (count <= 0) return [];
  const rowCount = Math.ceil(count / 4);
  const base = Math.floor(count / rowCount);
  const remainder = count % rowCount;
  return Array.from({ length: rowCount }, (_, index) => base + (index < remainder ? 1 : 0));
}

type CompactFrame = { card: LensCard; frame: LensCardLayout };

/**
 * Place compact frames in authoring order, preserving a simple left-to-right
 * grid while eliminating only unused vertical space. Smart arrange changes
 * the card presentation, not the analyst's implied reading order.
 */
function placeCompactRows(entries: Array<CompactFrame>, layouts: Map<string, LensCardLayout>, startY: number): number {
  let index = 0;
  let y = startY;
  while (index < entries.length) {
    const row: Array<CompactFrame> = [];
    let usedWidth = 0;
    while (index < entries.length) {
      const entry = entries[index];
      if (row.length > 0 && usedWidth + entry.frame.w > CANVAS_COLUMNS) {
        break;
      }
      row.push(entry);
      usedWidth += entry.frame.w;
      index += 1;
    }

    const rowHeight = Math.max(...row.map((entry) => entry.frame.h));
    let x = 0;
    row.forEach(({ card, frame }) => {
      layouts.set(card.id, { x, y, w: frame.w, h: frame.h });
      x += frame.w;
    });
    // Adjacent grid rows already receive LENS_FRAME_GAP_PX. Do not add an
    // empty coordinate row here or Smart arrange visibly reintroduces space.
    y += rowHeight;
  }
  return y;
}

/**
 * Rebuilds every frame from a readable dashboard grammar, then packs without
 * overlap. KPI tiles, charts and tables retain stable frames; text summaries
 * fit their measured content. Chart mark type and series count do not change
 * the dashboard's visual rhythm.
 */
export function smartArrangeCards(
  cards: Array<LensCard>,
  dataByCardId: Map<string, LensCardData>,
  insightHeights: ReadonlyMap<string, number> = new Map(),
): Array<LensCard> {
  const bySection = new Map<string, Array<LensCard>>();
  cards.forEach((card) => {
    const section = card.section ?? '__ungrouped';
    bySection.set(section, [...(bySection.get(section) ?? []), card]);
  });

  const layouts = new Map<string, LensCardLayout>();
  bySection.forEach((group) => {
    const headlineMetrics = group.filter((card) => card.type === 'metric' && !dataByCardId.get(card.id)?.truncated);
    let metricIndex = 0;
    let y = 0;
    for (const count of cardsPerBalancedRow(headlineMetrics.length)) {
      const width = CANVAS_COLUMNS / count;
      const height = SMART_METRIC_CANVAS_HEIGHT;
      headlineMetrics.slice(metricIndex, metricIndex + count).forEach((card, column) => {
        layouts.set(card.id, { x: column * width, y, w: width, h: height });
      });
      metricIndex += count;
      y += height;
    }

    const chartCards = group
      .filter((card) => !layouts.has(card.id) && card.type !== 'table')
      .map((card) => {
        const data = dataByCardId.get(card.id);
        return {
          card,
          frame:
            card.type === 'insight'
              ? {
                  x: 0,
                  y: 0,
                  w: CANVAS_COLUMNS,
                  // Keep long narratives scrollable instead of making one
                  // text card taller than an entire dashboard viewport.
                  h: Math.max(
                    minimumCanvasHeight(card),
                    frameRowSpan(Math.min(insightHeights.get(card.id) ?? 280, cardHeight('full', 'xtall').card)),
                  ),
                }
              : frameForCard({ ...card, size: 'large', height: 'tall' }, data),
        };
      });
    y = placeCompactRows(chartCards, layouts, y);

    const tables = group.filter((card) => card.type === 'table');
    placeCompactRows(
      tables.map((card) => {
        const data = dataByCardId.get(card.id);
        return {
          card,
          frame: frameForCard({ ...card, size: 'full', height: 'xtall' }, data),
        };
      }),
      layouts,
      y,
    );
  });

  return cards.map((card) => ({
    ...card,
    config: {
      ...card.config,
      titleAlignment: card.type === 'insight' ? (card.config?.titleAlignment ?? 'left') : 'center',
    },
    layout: layouts.get(card.id) ?? frameForCard(card, dataByCardId.get(card.id)),
  }));
}

/**
 * An addition may tidy an untouched dashboard, but it must never silently
 * rewrite a composition an analyst has started shaping. A dashboard with no
 * saved frames is untouched; once frames exist, they remain eligible only
 * while they still exactly match the layout Smart arrange would create.
 */
export function hasUntouchedSmartLayout(
  cards: ReadonlyArray<LensCard>,
  dataByCardId: Map<string, LensCardData> = new Map(),
): boolean {
  if (!cards.length || cards.every((card) => !card.layout)) {
    return true;
  }

  const smartById = new Map(smartArrangeCards([...cards], dataByCardId).map((card) => [card.id, card.layout]));
  return cards.every((card) => {
    const saved = card.layout;
    const smart = smartById.get(card.id);
    return Boolean(
      saved && smart && saved.x === smart.x && saved.y === smart.y && saved.w === smart.w && saved.h === smart.h,
    );
  });
}

/**
 * Gives an explicit multi-card selection equal-width columns while preserving
 * each card's height and section. Selected cards take precedence; cards they
 * displace flow to the next vacant frame, just as a direct canvas drop does.
 */
export function evenlyArrangeSelectedCards(
  cards: Array<LensCard>,
  selectedIds: ReadonlyArray<string>,
  dataByCardId: Map<string, LensCardData>,
): Array<LensCard> {
  const selected = new Set(selectedIds);
  if (selected.size < 2) return cards;

  const bySection = new Map<string, Array<LensCard>>();
  cards.forEach((card) => {
    const section = card.section ?? '__ungrouped';
    bySection.set(section, [...(bySection.get(section) ?? []), card]);
  });

  const arranged: Array<LensCard> = [];
  bySection.forEach((group) => {
    const chosen = group
      .filter((card) => selected.has(card.id))
      .sort(
        (left, right) =>
          (left.layout?.y ?? 0) - (right.layout?.y ?? 0) || (left.layout?.x ?? 0) - (right.layout?.x ?? 0),
      );
    if (chosen.length < 2) {
      arranged.push(...group);
      return;
    }

    const startY = Math.min(...chosen.map((card) => card.layout?.y ?? 0));
    let y = startY;
    let index = 0;
    const framed = new Map<string, LensCard>();

    for (const count of cardsPerBalancedRow(chosen.length)) {
      const row = chosen.slice(index, index + count);
      const width = count === 1 ? 6 : CANVAS_COLUMNS / count;
      const rowHeight = Math.max(
        ...row.map((card) => card.layout?.h ?? frameForCard(card, dataByCardId.get(card.id)).h),
      );
      row.forEach((card, column) => {
        framed.set(card.id, {
          ...card,
          layout: { x: column * width, y, w: width, h: card.layout?.h ?? rowHeight },
        });
      });
      index += count;
      y += rowHeight + 1;
    }

    // Resolve selected cards first so this command does exactly what it says;
    // neighbouring cards make room rather than silently defeating it.
    arranged.push(...chosen.map((card) => framed.get(card.id)!));
    arranged.push(...group.filter((card) => !selected.has(card.id)));
  });

  const resolved = resolveCanvasLayouts(arranged, dataByCardId);
  const byId = new Map(resolved.map((card) => [card.id, card]));
  return cards.map((card) => byId.get(card.id) ?? card);
}

/** The first row below everything already placed, where a new card belongs. */
export function nextCanvasRow(cards: ReadonlyArray<LensCard>): number {
  return cards.reduce((lowest, card) => Math.max(lowest, (card.layout?.y ?? 0) + (card.layout?.h ?? 1)), 0);
}

/**
 * A card's natural size, at a frame someone else chose.
 *
 * The blank card reserves a frame while it is being filled in, and its answer
 * is saved at that frame's origin, so the finished card appears exactly where
 * the blank one stood. Only the origin is kept: a metric that took the draft's
 * height as well would be a mostly empty box.
 */
export function frameAtOrigin(card: LensCard, origin: Pick<LensCardLayout, 'x' | 'y'>): LensCardLayout {
  return { ...frameForCard(card), x: origin.x, y: origin.y };
}

/** Puts the frames the canvas reports back onto the cards they belong to. */
/** Frames a gesture moved or resized are marked as the person's; the rest keep whoever placed them. */
export function framesPlacedByHand(
  frames: Record<string, LensCardLayout>,
  cards: Array<LensCard>,
): Record<string, LensCardLayout> {
  const before = new Map(cards.map((card) => [card.id, card.layout]));
  return Object.fromEntries(
    Object.entries(frames).map(([id, frame]) => {
      const was = before.get(id);
      const moved = !was || was.x !== frame.x || was.y !== frame.y || was.w !== frame.w || was.h !== frame.h;
      return [id, { ...frame, by: moved ? ('user' as const) : (was?.by ?? ('auto' as const)) }];
    }),
  );
}

export function applyCanvasFrames(cards: Array<LensCard>, frames: Record<string, LensCardLayout>): Array<LensCard> {
  return cards.map((card) => (frames[card.id] ? { ...card, layout: frames[card.id] } : card));
}

/**
 * Two cards of the same size trading places.
 *
 * Dropping one card onto another asks the engine to make room, so the card
 * underneath is pushed down and the row a reader was rearranging comes apart.
 * When the drop lands squarely on a single card of the same size, the obvious
 * reading is that the two should trade places, and that is what this returns.
 *
 * The two exchange frames entirely, position and size, so the board's shape is
 * unchanged and only which card sits in which slot differs. That is what makes
 * a trade between differently sized cards well defined: exchanging two occupied
 * frames can never overlap anything, whereas moving a wide chart to a narrow
 * card's corner would.
 */
export function swapTarget(
  cards: ReadonlyArray<LensCard>,
  moved: { i: string },
  dropped: LensCardLayout,
): LensCard | undefined {
  const origin = cards.find((card) => card.id === moved.i)?.layout;
  if (!origin) {
    return undefined;
  }

  // Overlap rather than the centre point, because the two cards are not
  // necessarily the same shape: a wide chart dragged onto a metric has its
  // centre nowhere near it, and a metric dragged onto a wide chart covers only
  // a corner of it. What both have in common is that one card is mostly on top
  // of the other.
  const ranked = cards
    .filter((card) => card.id !== moved.i && card.layout)
    .map((card) => ({ card, overlap: overlapArea(dropped, card.layout as LensCardLayout) }))
    .filter((entry) => entry.overlap > 0)
    .sort((left, right) => right.overlap - left.overlap);

  const [best, next] = ranked;
  if (!best) {
    return undefined;
  }
  // Mostly on top of one card, and more on it than on anything else. A drop
  // straddling two cards evenly is a move, not a trade.
  const smaller = Math.min(area(dropped), area(best.card.layout as LensCardLayout));
  if (best.overlap < smaller * 0.5 || (next && next.overlap >= best.overlap)) {
    return undefined;
  }
  return best.card;
}

function area(frame: LensCardLayout): number {
  return frame.w * frame.h;
}

function overlapArea(left: LensCardLayout, right: LensCardLayout): number {
  const width = Math.min(left.x + left.w, right.x + right.w) - Math.max(left.x, right.x);
  const height = Math.min(left.y + left.h, right.y + right.h) - Math.max(left.y, right.y);
  return width > 0 && height > 0 ? width * height : 0;
}

export function swapFrames(
  cards: ReadonlyArray<LensCard>,
  moved: { i: string } & LensCardLayout,
  dropped: LensCardLayout,
): Record<string, LensCardLayout> | undefined {
  const origin = cards.find((card) => card.id === moved.i)?.layout;
  const target = swapTarget(cards, moved, dropped);
  if (!origin || !target?.layout) {
    return undefined;
  }
  return Object.fromEntries(
    cards
      .filter((card) => card.layout)
      .map((card) => {
        if (card.id === moved.i) return [card.id, target.layout as LensCardLayout];
        if (card.id === target.id) return [card.id, origin];
        return [card.id, card.layout as LensCardLayout];
      }),
  );
}

/**
 * Project a saved frame into a narrower visual grid without ever allowing its
 * right edge to escape the canvas. Rounding the start down and the end up
 * preserves a card's share of the board: a wide chart remains at least as
 * wide a share as the KPI beside it.
 */
export function projectCanvasFrame(frame: LensCardLayout, columns: number): LensCardLayout {
  if (columns >= CANVAS_COLUMNS) {
    return { ...frame };
  }
  const safeColumns = Math.max(1, Math.round(columns));
  const start = Math.floor((frame.x / CANVAS_COLUMNS) * safeColumns);
  const end = Math.ceil(((frame.x + frame.w) / CANVAS_COLUMNS) * safeColumns);
  const x = Math.min(safeColumns - 1, Math.max(0, start));
  const w = Math.min(safeColumns - x, Math.max(1, end - x));
  return { x, y: Math.max(0, Math.round(frame.y)), w, h: Math.max(1, Math.round(frame.h)) };
}

/**
 * Translate an edit made on a multi-column projection back into the saved
 * twelve-column coordinate system. This is intentionally the inverse shape
 * of `projectCanvasFrame`: the visual left edge is kept, while the right edge
 * rounds outward so a resize can never create a narrower, clipped card on the
 * canonical board.
 */
export function restoreCanvasFrame(frame: LensCardLayout, columns: number): LensCardLayout {
  if (columns >= CANVAS_COLUMNS) {
    return { ...frame };
  }
  const safeColumns = Math.max(1, Math.round(columns));
  const start = Math.floor((frame.x / safeColumns) * CANVAS_COLUMNS);
  const end = Math.ceil(((frame.x + frame.w) / safeColumns) * CANVAS_COLUMNS);
  const x = Math.min(CANVAS_COLUMNS - 1, Math.max(0, start));
  const w = Math.min(CANVAS_COLUMNS - x, Math.max(1, end - x));
  return { x, y: Math.max(0, Math.round(frame.y)), w, h: Math.max(1, Math.round(frame.h)) };
}

/** Restores a whole projected layout before it is persisted to the Lens. */
export function restoreCanvasFrames(
  frames: Record<string, LensCardLayout>,
  columns: number,
): Record<string, LensCardLayout> {
  return Object.fromEntries(Object.entries(frames).map(([id, frame]) => [id, restoreCanvasFrame(frame, columns)]));
}

/**
 * Render-safe frames for a measured canvas width.
 *
 * This deliberately does not write anything back to the Lens. A narrow
 * screen is a different view of the same dashboard, not permission to turn a
 * carefully arranged desktop board into a one-column saved layout. A phone
 * gets a simple reading-order stack; the authored desktop layout is never
 * changed or compacted as a consequence.
 */
export function projectCanvasFrames(
  cards: ReadonlyArray<LensCard>,
  frameFor: (card: LensCard) => LensCardLayout,
  columns: number,
): Record<string, LensCardLayout> {
  const safeColumns = Math.min(CANVAS_COLUMNS, Math.max(1, Math.round(columns)));
  if (safeColumns === CANVAS_COLUMNS) {
    return Object.fromEntries(cards.map((card) => [card.id, frameFor(card)]));
  }
  return stackFrames(cards, frameFor);
}

/**
 * Projects saved frames onto a single column, preserving reading order.
 *
 * Order comes from the saved coordinates rather than the card array, so the
 * column reads down the board the way the board reads across: top row first,
 * left to right within a row. Heights carry over untouched, so a card that
 * was given room for a tall table keeps it.
 */
export function stackFrames(
  cards: ReadonlyArray<LensCard>,
  frameFor: (card: LensCard) => LensCardLayout,
): Record<string, LensCardLayout> {
  const ordered = cards
    .map((card) => ({ card, frame: frameFor(card) }))
    .sort((left, right) => left.frame.y - right.frame.y || left.frame.x - right.frame.x);

  const stacked: Record<string, LensCardLayout> = {};
  let y = 0;
  for (const { card, frame } of ordered) {
    stacked[card.id] = { x: 0, y, w: 1, h: frame.h };
    y += frame.h;
  }
  return stacked;
}
